import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface UserSession {
  id: string;
  user_id: string;
  user_role: string;
  ip_address: string | null;
  user_agent: string | null;
  device_info: any;
  login_time: string;
  logout_time: string | null;
  is_active: boolean;
  actions_performed: any;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

interface ActivityLog {
  id: string;
  action_type: string;
  resource_type: string;
  resource_id: string | null;
  patient_id: string | null;
  details: any;
  created_at: string;
  ip_address: string | null;
}

const ACTION_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  login: { icon: 'ri-login-box-line', color: 'text-green-600', bg: 'bg-green-100' },
  logout: { icon: 'ri-logout-box-line', color: 'text-red-600', bg: 'bg-red-100' },
  view: { icon: 'ri-eye-line', color: 'text-blue-600', bg: 'bg-blue-100' },
  create: { icon: 'ri-add-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  update: { icon: 'ri-edit-line', color: 'text-amber-600', bg: 'bg-amber-100' },
  delete: { icon: 'ri-delete-bin-line', color: 'text-red-600', bg: 'bg-red-100' },
  search: { icon: 'ri-search-line', color: 'text-purple-600', bg: 'bg-purple-100' },
  export: { icon: 'ri-download-line', color: 'text-indigo-600', bg: 'bg-indigo-100' },
  import: { icon: 'ri-upload-line', color: 'text-orange-600', bg: 'bg-orange-100' },
  access: { icon: 'ri-folder-open-line', color: 'text-cyan-600', bg: 'bg-cyan-100' },
  navigate: { icon: 'ri-compass-3-line', color: 'text-gray-600', bg: 'bg-gray-100' },
};

const ROLE_TABLES: Record<string, string> = {
  medecin: 'medecins',
  patient: 'patients',
  secretaire: 'secretaires',
  super_admin: 'super_admins',
  laborantin: 'laborantins',
  sage_femme: 'sage_femmes',
};

function getActionDisplay(action: string): { icon: string; color: string; bg: string } {
  return ACTION_ICONS[action] || { icon: 'ri-record-circle-line', color: 'text-gray-500', bg: 'bg-gray-100' };
}

function getResourceLabel(type: string): string {
  const labels: Record<string, string> = {
    patient: 'Patient', dossier: 'Dossier', rendez_vous: 'RDV',
    prescription: 'Ordonnance', bon_laboratoire: 'Bon labo',
    analyse: 'Analyse', medicament: 'Médicament', actualite: 'Actualité',
    message: 'Message', notification: 'Notification', configuration: 'Configuration',
    user: 'Utilisateur', session: 'Session', export_data: 'Export',
    dashboard: 'Tableau de bord', profile: 'Profil', page: 'Navigation',
    secretaire: 'Secrétaire', medecin: 'Médecin', laborantin: 'Laborantin',
    sage_femme: 'Sage-femme', admin: 'Administrateur',
  };
  return labels[type] || type;
}

export default function SessionsPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [dbFilters, setDbFilters] = useState({
    userRole: '',
    dateFrom: '',
    dateTo: '',
    activeOnly: false,
  });
  const [searchUser, setSearchUser] = useState('');
  const [selectedSession, setSelectedSession] = useState<UserSession | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'super_admin')) {
      navigate('/login', { replace: true });
    }
  }, [user, role, authLoading, navigate]);

  // Charge les sessions avec lookup batch des profils
  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('user_sessions')
        .select('*')
        .order('login_time', { ascending: false })
        .limit(500);

      if (dbFilters.userRole) {
        query = query.eq('user_role', dbFilters.userRole);
      }
      if (dbFilters.activeOnly) {
        query = query.eq('is_active', true);
      }
      if (dbFilters.dateFrom) {
        query = query.gte('login_time', new Date(dbFilters.dateFrom).toISOString());
      }
      if (dbFilters.dateTo) {
        const endDate = new Date(dbFilters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('login_time', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const rawSessions = data || [];
      if (rawSessions.length === 0) {
        setSessions([]);
        setLoading(false);
        return;
      }

      // --- BATCH PROFILE LOOKUP (remplace les N+1 requêtes) ---
      // Grouper les user_ids par rôle
      const idsByRole: Record<string, string[]> = {};
      for (const s of rawSessions) {
        if (!idsByRole[s.user_role]) idsByRole[s.user_role] = [];
        if (!idsByRole[s.user_role].includes(s.user_id)) {
          idsByRole[s.user_role].push(s.user_id);
        }
      }

      // Une seule requête par table de rôle avec .in()
      const profileMap: Record<string, { email?: string; name?: string }> = {};
      await Promise.all(
        Object.entries(idsByRole).map(async ([role, userIds]) => {
          const table = ROLE_TABLES[role];
          if (!table) return;

          const selectFields = role === 'super_admin'
            ? 'user_id, nom, prenom'
            : 'user_id, email, nom, prenom';

          const { data: profiles } = await supabase
            .from(table)
            .select(selectFields)
            .in('user_id', userIds);

          if (profiles) {
            for (const p of profiles) {
              const fullName = role === 'medecin'
                ? `Dr ${p.prenom || ''} ${p.nom || ''}`
                : `${p.prenom || ''} ${p.nom || ''}`;
              profileMap[p.user_id] = {
                email: p.email || undefined,
                name: fullName.trim() || undefined,
              };
            }
          }

          // Pour super_admin, récupérer l'email via auth (peu de super_admins donc acceptable)
          if (role === 'super_admin' && userIds.length > 0) {
            for (const uid of userIds) {
              try {
                const { data: { user: authUser } } = await supabase.auth.admin.getUser(uid);
                if (authUser?.email && profileMap[uid]) {
                  profileMap[uid].email = authUser.email;
                }
              } catch { /* silencieux */ }
            }
          }
        })
      );

      // Enrichir les sessions avec les profils
      const enrichedSessions: UserSession[] = rawSessions.map((s) => {
        const profile = profileMap[s.user_id];
        return {
          ...s,
          user_email: profile?.email,
          user_name: profile?.name,
        };
      });

      setSessions(enrichedSessions);
    } catch (error) {
      console.error('Erreur chargement sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [dbFilters]);

  useEffect(() => {
    if (user && role === 'super_admin') {
      loadSessions();
    }
  }, [user, role, loadSessions]);

  // Filtrage côté client uniquement pour la recherche texte
  const filteredSessions = searchUser
    ? sessions.filter((s) => {
        const lower = searchUser.toLowerCase();
        return (
          s.user_email?.toLowerCase().includes(lower) ||
          s.user_name?.toLowerCase().includes(lower)
        );
      })
    : sessions;

  const openSessionDetails = async (session: UserSession) => {
    setSelectedSession(session);
    setActivityLoading(true);
    setActivityLogs([]);

    try {
      let query = supabase
        .from('access_logs')
        .select('*')
        .eq('user_id', session.user_id)
        .order('created_at', { ascending: true })
        .limit(200);

      if (session.login_time) {
        query = query.gte('created_at', session.login_time);
      }
      if (session.logout_time) {
        query = query.lte('created_at', session.logout_time);
      } else if (!session.is_active) {
        const maxEnd = new Date(session.login_time);
        maxEnd.setHours(maxEnd.getHours() + 24);
        query = query.lte('created_at', maxEnd.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      setActivityLogs(data || []);
    } catch (err) {
      console.error('Erreur chargement activité:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  const getDeviceIcon = (userAgent: string | null) => {
    if (!userAgent) return 'ri-computer-line';
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return 'ri-smartphone-line';
    if (ua.includes('tablet') || ua.includes('ipad')) return 'ri-tablet-line';
    return 'ri-computer-line';
  };

  const getBrowserInfo = (userAgent: string | null) => {
    if (!userAgent) return 'Inconnu';
    const ua = userAgent.toLowerCase();
    if (ua.includes('edg')) return 'Edge';
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari')) return 'Safari';
    if (ua.includes('opera')) return 'Opera';
    return 'Autre';
  };

  const getSessionDuration = (loginTime: string, logoutTime: string | null, isActive: boolean) => {
    const login = new Date(loginTime);
    const logout = logoutTime ? new Date(logoutTime) : (isActive ? new Date() : login);
    if (!logoutTime && !isActive) return 'N/C';
    const diff = Math.abs(logout.getTime() - login.getTime());
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  const getSessionStatus = (session: UserSession) => {
    if (session.is_active) {
      const loginDate = new Date(session.login_time);
      const hoursSinceLogin = (Date.now() - loginDate.getTime()) / (1000 * 60 * 60);
      if (hoursSinceLogin > 24) {
        return { label: 'Expirée', color: 'bg-amber-50 text-amber-600', dot: 'bg-amber-400' };
      }
      if (hoursSinceLogin > 1 && !session.logout_time) {
        return { label: 'Inactive', color: 'bg-yellow-50 text-yellow-600', dot: 'bg-yellow-400' };
      }
      return { label: 'Active', color: 'bg-green-50 text-green-600', dot: 'bg-green-400 animate-pulse' };
    }
    return { label: 'Terminée', color: 'bg-gray-50 text-gray-500', dot: 'bg-gray-300' };
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const formatTimeOnly = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const exportSessions = () => {
    const csv = [
      ['Date connexion', 'Date déconnexion', 'Utilisateur', 'Rôle', 'IP', 'Navigateur', 'Durée', 'Statut'].join(','),
      ...filteredSessions.map((session) =>
        [
          formatDateTime(session.login_time),
          session.logout_time ? formatDateTime(session.logout_time) : '-',
          session.user_name || session.user_email || session.user_id,
          session.user_role,
          session.ip_address || '-',
          getBrowserInfo(session.user_agent),
          getSessionDuration(session.login_time, session.logout_time, session.is_active),
          getSessionStatus(session).label,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Appliquer les filtres DB (sans recharger immédiatement — le useEffect s'en charge)
  const applyFilters = (newFilters: typeof dbFilters) => {
    setDbFilters(newFilters);
  };

  const activeCount = sessions.filter(s => s.is_active).length;
  const todayCount = sessions.filter(s => new Date(s.login_time).toDateString() === new Date().toDateString()).length;
  const activeMedecins = sessions.filter(s => s.user_role === 'medecin' && s.is_active).length;
  const activePatients = sessions.filter(s => s.user_role === 'patient' && s.is_active).length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-clinic-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-clinic-blue hover:text-clinic-blue-dark mb-4 flex items-center cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Retour au tableau de bord
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-clinic-blue-dark">Historique des Connexions</h1>
              <p className="text-gray-600 mt-2">Suivi complet des sessions utilisateurs et activités</p>
            </div>
            <button
              onClick={exportSessions}
              className="px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg flex items-center whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-2"></i>
              Exporter CSV
            </button>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-100">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Sessions actives</p><p className="text-3xl font-bold text-green-600">{activeCount}</p></div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"><i className="ri-user-line text-2xl text-green-600"></i></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-blue-100">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Aujourd&apos;hui</p><p className="text-3xl font-bold text-blue-600">{todayCount}</p></div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><i className="ri-calendar-line text-2xl text-blue-600"></i></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-purple-100">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Médecins connectés</p><p className="text-3xl font-bold text-purple-600">{activeMedecins}</p></div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><i className="ri-stethoscope-line text-2xl text-purple-600"></i></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-orange-100">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-gray-600 mb-1">Patients connectés</p><p className="text-3xl font-bold text-orange-600">{activePatients}</p></div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"><i className="ri-user-heart-line text-2xl text-orange-600"></i></div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-clinic-blue-dark mb-4">Filtres</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
              <select value={dbFilters.userRole} onChange={(e) => applyFilters({ ...dbFilters, userRole: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm">
                <option value="">Tous</option>
                <option value="patient">Patients</option>
                <option value="medecin">Médecins</option>
                <option value="secretaire">Secrétaires</option>
                <option value="super_admin">Administrateurs</option>
                <option value="laborantin">Laborantins</option>
                <option value="sage_femme">Sages-femmes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
              <input type="date" value={dbFilters.dateFrom} onChange={(e) => applyFilters({ ...dbFilters, dateFrom: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
              <input type="date" value={dbFilters.dateTo} onChange={(e) => applyFilters({ ...dbFilters, dateTo: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
              <input type="text" placeholder="Email ou nom..." value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <div className="flex items-center h-10">
                <input type="checkbox" id="activeOnly" checked={dbFilters.activeOnly}
                  onChange={(e) => applyFilters({ ...dbFilters, activeOnly: e.target.checked })}
                  className="w-5 h-5 text-clinic-blue border-gray-300 rounded focus:ring-clinic-blue cursor-pointer" />
                <label htmlFor="activeOnly" className="ml-3 text-sm text-gray-700 cursor-pointer">Actives uniquement</label>
              </div>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-clinic-blue/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Utilisateur</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Connexion</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Déconnexion</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Durée</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Appareil</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">IP</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Statut</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Activité</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSessions.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <i className="ri-file-list-line text-4xl mb-2 block"></i>Aucune session trouvée
                  </td></tr>
                ) : (
                  filteredSessions.map((session) => {
                    const status = getSessionStatus(session);
                    return (
                      <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm">
                          <div className="font-medium text-gray-900">{session.user_name || session.user_email || 'Utilisateur inconnu'}</div>
                          <div className="text-xs text-gray-500 capitalize">{session.user_role?.replace('_', ' ')}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{formatDateTime(session.login_time)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {session.logout_time ? formatDateTime(session.logout_time) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {getSessionDuration(session.login_time, session.logout_time, session.is_active)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <i className={`${getDeviceIcon(session.user_agent)} text-gray-600`}></i>
                            <span className="text-gray-700">{getBrowserInfo(session.user_agent)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 font-mono text-xs">{session.ip_address || '-'}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                            <span className={`w-2 h-2 ${status.dot} rounded-full mr-2`}></span>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => openSessionDetails(session)}
                            className="text-clinic-blue hover:text-clinic-blue-dark cursor-pointer font-medium text-sm flex items-center gap-1"
                          >
                            <i className="ri-eye-line"></i>
                            <span className="hidden sm:inline">Détails</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal détails session avec timeline d'activité */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* En-tête */}
            <div className="sticky top-0 bg-white rounded-t-3xl z-10 p-6 pb-4 border-b border-gray-100">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedSession.user_name || 'Utilisateur inconnu'}
                  </h3>
                  <p className="text-sm text-gray-500 capitalize mt-0.5">
                    {selectedSession.user_role?.replace('_', ' ')} · {selectedSession.user_email || 'Email inconnu'}
                  </p>
                </div>
                <button onClick={() => { setSelectedSession(null); setActivityLogs([]); }}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-full hover:bg-gray-100">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Info bar */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-1.5 text-xs bg-gray-100 rounded-full px-3 py-1.5">
                  <i className="ri-login-box-line text-green-600"></i>
                  <span className="text-gray-600">Connexion:</span>
                  <span className="font-medium text-gray-800">{formatTimeOnly(selectedSession.login_time)}</span>
                </div>
                {selectedSession.logout_time ? (
                  <div className="flex items-center gap-1.5 text-xs bg-gray-100 rounded-full px-3 py-1.5">
                    <i className="ri-logout-box-line text-red-600"></i>
                    <span className="text-gray-600">Déconnexion:</span>
                    <span className="font-medium text-gray-800">{formatTimeOnly(selectedSession.logout_time)}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs bg-green-100 rounded-full px-3 py-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-green-700 font-medium">Session en cours</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs bg-gray-100 rounded-full px-3 py-1.5">
                  <i className="ri-time-line text-gray-500"></i>
                  <span className="text-gray-600">Durée:</span>
                  <span className="font-medium text-gray-800">
                    {getSessionDuration(selectedSession.login_time, selectedSession.logout_time, selectedSession.is_active)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs bg-gray-100 rounded-full px-3 py-1.5">
                  <i className={`${getDeviceIcon(selectedSession.user_agent)} text-gray-500`}></i>
                  <span className="text-gray-600">{getBrowserInfo(selectedSession.user_agent)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs bg-gray-100 rounded-full px-3 py-1.5 font-mono">
                  <span className="text-gray-500">IP:</span>
                  <span className="text-gray-700">{selectedSession.ip_address || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Timeline d'activité */}
            <div className="p-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-history-line text-lg text-clinic-blue"></i>
                Journal d'activité
                {activityLogs.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">({activityLogs.length} actions)</span>
                )}
              </h4>

              {activityLoading ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 border-3 border-clinic-blue border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Chargement des activités...</p>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <i className="ri-inbox-line text-4xl text-gray-300 block mb-2"></i>
                  <p className="text-sm text-gray-500">Aucune activité enregistrée pour cette session</p>
                  <p className="text-xs text-gray-400 mt-1">Les actions de navigation et de consultation apparaîtront ici</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Ligne verticale de timeline */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gray-200"></div>

                  <div className="space-y-1">
                    {activityLogs.map((log, idx) => {
                      const actionDisplay = getActionDisplay(log.action_type);
                      return (
                        <div key={log.id || idx} className="flex items-start gap-4 py-2 relative">
                          {/* Point sur la timeline */}
                          <div className={`w-[38px] h-[38px] ${actionDisplay.bg} rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 border-white`}>
                            <i className={`${actionDisplay.icon} ${actionDisplay.color}`}></i>
                          </div>

                          <div className="flex-1 min-w-0 pt-1.5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-800 capitalize">
                                  {log.action_type}
                                </span>
                                <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                  {getResourceLabel(log.resource_type)}
                                </span>
                                {log.resource_id && (
                                  <span className="text-xs text-gray-400 font-mono truncate max-w-[120px]">
                                    {log.resource_id.substring(0, 8)}...
                                  </span>
                                )}
                                {log.patient_id && (
                                  <span className="text-xs text-clinic-blue bg-clinic-blue/10 rounded-full px-2 py-0.5">
                                    <i className="ri-user-line mr-1"></i>Patient
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {formatTimeOnly(log.created_at)}
                              </span>
                            </div>

                            {log.details && Object.keys(log.details).length > 0 && (
                              <div className="mt-1.5 bg-gray-50 rounded-lg p-2.5 text-xs text-gray-600">
                                {Object.entries(log.details).map(([key, val]) => (
                                  <div key={key} className="flex gap-2">
                                    <span className="text-gray-400 font-medium min-w-[80px]">{key}:</span>
                                    <span className="text-gray-700 truncate">
                                      {typeof val === 'object' ? JSON.stringify(val).substring(0, 100) : String(val).substring(0, 150)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 pt-0">
              <button
                onClick={() => { setSelectedSession(null); setActivityLogs([]); }}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}